<template>
  <div class="video-generation-library">
    <div class="d-flex align-items-center justify-content-end">
      <btn-loading
        class="width-max"
        variant-convert="btn-submit"
        type="submit"
        pill
        :disabled="videosSelected.length === 0"
        @click="handleSyncVideoToAdAccount"
      >
        Upload to ad account
      </btn-loading>
    </div>
    <!-- Video Library Grid -->
    <div class="video-library-grid">
      <div class="waterfall-container justify-content-center">
        <!-- Loading overlay for video list -->
        <div
          v-if="videoListLoading"
          class="grid-loading-overlay"
        >
          <div class="loading-content">
            <div class="loading-spinner">
              <feather-icon
                icon="LoaderIcon"
                size="32"
                class="spinning"
              />
            </div>
          </div>
        </div>

        <!-- Video Cards -->
        <div
          v-for="video in videoListVideos"
          :key="video.id"
          class="waterfall-column"
        >
          <div class="video-library-card">
            <div
              class="video-container"
              @mouseleave="pauseVideoOnHoverOut(video.id)"
            >
              <!-- Thumbnail Image (default display) -->
              <img
                v-if="!videoPlayingStates[video.id]"
                :src="video.imageUrl || (video.thumbnailUrl)"
                :alt="video.title"
                class="video-thumbnail"
                @click="playVideo(video.id)"
              >

              <!-- Video Element (only when playing) -->
              <video
                v-if="videoPlayingStates[video.id]"
                :src="video.videoPolling && video.videoPolling.videoUrl"
                :data-video-id="video.id"
                class="generated-video"
                preload="metadata"
                autoplay
                :muted="isVideoMuted(video.id)"
                @click="toggleVideo(video.id)"
                @ended="onVideoEnded(video.id)"
              >
                Your browser does not support the video tag.
              </video>

              <!-- Custom Play Button (only on thumbnail) -->
              <div
                v-if="!videoPlayingStates[video.id] && video.videoPolling && video.videoPolling.videoUrl"
                class="custom-play-button"
                @click="playVideo(video.id)"
              >
                <feather-icon
                  icon="PlayIcon"
                  size="32"
                  class="play-icon"
                />
              </div>

              <!-- Mute/Unmute Toggle (visible on hover when video exists) -->
              <div
                v-if="video.videoPolling && video.videoPolling.videoUrl"
                class="mute-toggle"
                @click.stop="toggleMute(video.id)"
              >
                <feather-icon
                  :icon="isVideoMuted(video.id) ? 'VolumeXIcon' : 'Volume2Icon'"
                  size="18"
                />
              </div>

              <!-- Download Button (visible on hover when video exists) -->
              <div
                v-if="video.videoPolling && video.videoPolling.videoUrl"
                class="download-button"
                @click.stop="downloadVideo(video)"
              >
                <feather-icon
                  icon="DownloadIcon"
                  size="18"
                />
              </div>

              <div
                class="select-checkbox cursor-pointer"
                :class="{ checked: isVideoSelected(video) }"
                @click="selectVideo(video)"
              >
                <feather-icon
                  v-if="isVideoSelected(video)"
                  icon="CheckIcon"
                  size="14"
                  class="text-white"
                />
              </div>

              <div
                :id="`popover-target-${video.id}`"
                style="position: absolute; bottom: 10px;left: 10px;z-index: 3;"
              >
                <div
                  v-if="advertiserInfo(video).length > 0"
                  class="advertiser-info cursor-pointer"
                >
                  Advertiser info
                </div>
              </div>

              <b-popover
                :target="`popover-target-${video.id}`"
                triggers="hover"
                placement="top"
              >
                <p
                  v-for="id in advertiserInfo(video)"
                  :key="id"
                  class="mb-0 font-14 text-center"
                >
                  {{ id }}
                </p>
              </b-popover>

            </div>
            <div class="video-info">
              <h4>{{ video.title || 'Untitled Video' }}</h4>
              <p>{{ video.description || 'No description' }}</p>
            </div>
          </div>
        </div>

        <!-- Empty State -->
        <div
          v-if="!videoListLoading && videoListVideos.length === 0"
          class="empty-state"
        >
          <div class="empty-state-content">
            <feather-icon
              icon="VideoIcon"
              size="48"
              class="empty-icon"
            />
            <h3 class="empty-title">
              No videos found
            </h3>
            <p class="empty-description">
              Your generated videos will appear here once you create them.
            </p>
            <b-button
              variant="primary"
              @click="$router.push('/video-generation')"
            >
              Create Video
            </b-button>
          </div>
        </div>
      </div>
    </div>
    <!-- Pagination -->
    <div class="pagination-container d-flex align-items-center justify-content-end mt-1 mb-2">
      <div class="page-size-control mr-1">
        <span class="page-size-label">Per page:</span>
        <b-form-select
          :options="pageSizeOptions"
          :value="selectedPageSize"
          size="sm"
          class="page-size-select"
          @change="onPageSizeChange"
        />
      </div>
      <b-pagination
        v-model="dummyModelForVue2"
        :value="currentPage"
        :total-rows="totalVideos"
        :per-page="pageSize"
        :limit="5"
        size="sm"
        first-number
        last-number
        prev-class="prev-item"
        next-class="next-item"
        class="pagination mt-1 mt-md-0 pagination-table b-pagination"
        :disabled="videoListLoading"
        @change="onPageChange"
      >
        <template #prev-text>
          <feather-icon
            icon="ChevronLeftIcon"
            size="18"
          />
        </template>
        <template #next-text>
          <feather-icon
            icon="ChevronRightIcon"
            size="18"
          />
        </template>
      </b-pagination>
    </div>

    <confirm-sync-video-modal
      ref="open-confirm-sync-video-modal"
      :videos-selected="videosSelected"
      :ad-accounts="adAccounts"
      @refreshLibrary="refreshLibrary"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions, createNamespacedHelpers } from 'vuex'
import {
  BButton, BPagination, BFormSelect, BPopover,
  VBPopover,
} from 'bootstrap-vue'

import BtnLoading from '@/layouts/components/BtnLoading.vue'
import ConfirmSyncVideoModal from '@/views/video-generation-2/components/ConfirmSyncVideoModal.vue'

const { mapActions: mapAdAccountActions, mapGetters: mapAdAccountGetters } = createNamespacedHelpers('adsAccount')
export default {
  name: 'VideoGenerationLibrary',
  components: {
    ConfirmSyncVideoModal,
    BtnLoading,
    BPopover,
    BButton,
    BPagination,
    BFormSelect,
  },
  directives: {
    'b-popover': VBPopover,
  },
  data() {
    return {
      libraryTypes: ['AVATAR_PRODUCT', 'VOICEOVER', 'DUBBING', 'MANUAL_UPLOAD_ADS_VIDEO'],
      // Dummy model to satisfy b-pagination v-model in Vue 2 without mutating store state directly
      dummyModelForVue2: 1,
      pageSizeOptions: [5, 10, 20, 50],
      selectedPageSize: 20,
      videoPlayingStates: {},
      videoMutedStates: {},
      videosSelected: [],
    }
  },
  computed: {
    ...mapGetters('videoGeneration', [
      'videoListVideos',
      'videoListLoading',
      'videoListError',
      'videoListPagination',
    ]),
    ...mapAdAccountGetters(['listAccount']),
    advertiserInfo() {
      return video => video?.videoGeneration?.result?.advertiserIds || []
    },

    adAccounts() {
      return this.listAccount?.content || []
    },

    currentPage() {
      return this.videoListPagination?.page || 1
    },
    pageSize() {
      return this.videoListPagination?.limit || 20
    },
    totalVideos() {
      return this.videoListPagination?.total || 0
    },
  },
  async mounted() {
    try {
      await this.fetchVideoList({
        page: 1,
        limit: 20,
        types: this.libraryTypes,
      })
      this.selectedPageSize = this.pageSize
    } catch (error) {
      this.$toast.error('Failed to load videos from server')
    }

    if (this.listAccount && this.listAccount?.content?.length === 0) {
      const params = {
        page: 1,
        limit: 100,
        status: 'SHOW_ACCOUNT_STATUS_APPROVED',
      }
      await this.getAccountList(params)
    }
  },
  methods: {
    cleanUrl(url) {
      if (!url) return ''
      return String(url).replace(/^@/, '').replace(/`/g, '').trim()
    },
    ...mapActions('videoGeneration', [
      'fetchVideoList',
    ]),
    ...mapAdAccountActions(['getAccountList']),

    async refreshLibrary() {
      this.videosSelected = []
      const page = this.videoListPagination?.page || 1
      const limit = this.videoListPagination?.limit || 20
      try {
        await this.fetchVideoList({ page, limit, types: this.libraryTypes })
      } catch (error) {
        this.$toast.error('Failed to refresh videos')
      }
    },
    async onPageChange(page) {
      try {
        await this.fetchVideoList({ page, limit: this.pageSize, types: this.libraryTypes })
      } catch (error) {
        this.$toast.error('Failed to load videos for this page')
      }
    },
    async onPageSizeChange(limit) {
      try {
        this.selectedPageSize = limit
        await this.fetchVideoList({ page: 1, limit, types: this.libraryTypes })
      } catch (error) {
        this.$toast.error('Failed to change page size')
      }
    },
    playVideo(videoId) {
      const videoData = (this.videoListVideos || []).find(v => v.id === videoId)
      const canPlay = videoData && videoData.videoPolling && videoData.videoPolling.videoUrl
      if (!canPlay) return
      this.$set(this.videoPlayingStates, videoId, true)
      this.$nextTick(() => {
        const videoElement = this.$el.querySelector(`video[data-video-id="${videoId}"]`)
        if (videoElement) {
          videoElement.play().catch(() => {
            this.$set(this.videoPlayingStates, videoId, false)
          })
        }
      })
    },
    pauseVideo(videoId) {
      const videoElement = this.$el.querySelector(`video[data-video-id="${videoId}"]`)
      if (videoElement) videoElement.pause()
      this.$set(this.videoPlayingStates, videoId, false)
    },
    toggleVideo(videoId) {
      const isPlaying = this.videoPlayingStates[videoId]
      if (isPlaying) this.pauseVideo(videoId)
      else this.playVideo(videoId)
    },
    onVideoEnded(videoId) {
      this.$set(this.videoPlayingStates, videoId, false)
    },
    pauseVideoOnHoverOut(videoId) {
      if (this.videoPlayingStates[videoId]) {
        this.pauseVideo(videoId)
      }
    },
    isVideoMuted(videoId) {
      return !!this.videoMutedStates[videoId]
    },
    toggleMute(videoId) {
      const nextState = !this.isVideoMuted(videoId)
      this.$set(this.videoMutedStates, videoId, nextState)
      const el = this.$el.querySelector(`video[data-video-id="${videoId}"]`)
      if (el) el.muted = nextState
    },
    async downloadVideo(video) {
      try {
        const rawUrl = (video && video.videoPolling && video.videoPolling.videoUrl) || ''
        const cleanedUrl = this.cleanUrl(rawUrl)
        if (!cleanedUrl) {
          this.$toast.error('Invalid URL for video')
          return
        }

        const response = await fetch(cleanedUrl)
        if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`)

        const blob = await response.blob()
        if (!blob || blob.size === 0) throw new Error('Downloaded file is empty')

        const blobUrl = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = blobUrl
        const filename = (cleanedUrl.split('/').pop() || '').split('?')[0] || `${video.id || 'video'}.mp4`
        a.download = filename
        a.rel = 'noopener'
        document.body.appendChild(a)
        a.click()

        setTimeout(() => {
          document.body.removeChild(a)
          URL.revokeObjectURL(blobUrl)
          this.$toast.success('Video downloaded')
        }, 1000)
      } catch {
        this.$toast.error('Failed to download')
      }
    },

    isVideoSelected(video) {
      return this.videosSelected.some(v => v.id === video.id)
    },

    selectVideo(video) {
      const index = this.videosSelected.findIndex(v => v.id === video.id)
      if (index > -1) {
        this.videosSelected.splice(index, 1)
      } else {
        this.videosSelected.push(video)
      }
    },

    async handleSyncVideoToAdAccount() {
      this.$refs['open-confirm-sync-video-modal'].showModal()
    },
  },
}
</script>

<style lang="scss" scoped>
.video-generation-library {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 24px;
  background-color: #ffffff;
  border-radius: 32px;

  .video-library-grid {
    margin-top: 8px;

    .waterfall-container {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
      gap: 16px;
      position: relative;
    }

    .waterfall-column {
      display: flex;
      flex-direction: column;
      min-width: 0;
      max-width: 280px;
      width: 100%;
    }

    .grid-loading-overlay {
      position: absolute;
      inset: 0;
      background: rgba(15, 23, 42, 0.6);
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 5;

      .loading-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;
        color: #E5E7EB;
      }
    }

    .empty-state {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 400px;
      width: 100%;

      .empty-state-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 16px;
        text-align: center;

        .empty-icon {
          color: #9CA3AF;
        }

        .empty-title {
          font-size: 24px;
          font-weight: 600;
          color: #374151;
          margin: 0;
        }

        .empty-description {
          font-size: 16px;
          color: #6B7280;
          margin: 0;
        }
      }
    }

    @media (max-width: 1400px) {
      .waterfall-container {
        grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
      }
    }

    @media (max-width: 1200px) {
      .waterfall-container {
        grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
      }
    }

    @media (max-width: 900px) {
      .waterfall-container {
        grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
      }
    }

    @media (max-width: 768px) {
      .waterfall-container {
        grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
        gap: 12px;
      }
    }

    @media (max-width: 480px) {
      .waterfall-container {
        grid-template-columns: 1fr;
      }
    }
  }

  .video-library-card {
    display: flex;
    flex-direction: column;
    gap: 0;
    background: #FFFFFF;
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    width: 100%;
    max-width: 280px;
    aspect-ratio: 9/16;
    height: auto;
    overflow: hidden;

    .video-container {
      position: relative;
      width: 100%;
      height: 0;
      padding-bottom: 177.78%;
      border-radius: 8px;
      overflow: hidden;
      background: #000;
      flex: 1;

      .video-thumbnail {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: block;
        object-fit: cover;
        cursor: pointer;
      }

      .generated-video {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        display: block;
        object-fit: cover;
        cursor: pointer;
      }

      .custom-play-button {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        z-index: 2;
        cursor: pointer;
        transition: all 0.3s ease;
        background: rgba(0, 0, 0, 0.7);
        border-radius: 50%;
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        backdrop-filter: blur(4px);
        opacity: 0;
        pointer-events: none;

        &:hover {
          background: rgba(0, 0, 0, 0.8);
          transform: translate(-50%, -50%) scale(1.08);
        }

        .play-icon {
          color: #ffffff;
          margin-left: 2px;
        }
      }

      .mute-toggle {
        position: absolute;
        top: 10px;
        left: 10px;
        z-index: 3;
        width: 28px;
        height: 28px;
        border-radius: 50%;
        background: rgba(0, 0, 0, 0.6);
        display: flex;
        align-items: center;
        justify-content: center;
        color: #fff;
        cursor: pointer;
        opacity: 0;
        pointer-events: none;
        transition: all 0.2s ease;

        &:hover {
          background: rgba(0, 0, 0, 0.75);
          transform: scale(1.06);
        }
      }

      .download-button {
        position: absolute;
        top: 10px;
        right: 46px;
        z-index: 3;
        width: 28px;
        height: 28px;
        border-radius: 50%;
        background: rgba(0, 0, 0, 0.6);
        display: flex;
        align-items: center;
        justify-content: center;
        color: #fff;
        cursor: pointer;
        opacity: 0;
        pointer-events: none;
        transition: all 0.2s ease;

        &:hover {
          background: rgba(0, 0, 0, 0.75);
          transform: scale(1.06);
        }
      }

      .select-checkbox {
        position: absolute;
        top: 10px;
        right: 10px;
        width: 24px;
        height: 24px;
        border-radius: 4px;
        border: 1px solid rgba(255, 255, 255, 0.9);
        background: rgba(0, 0, 0, 0.15);
        display: flex;
        align-items: center;
        justify-content: center;
        backdrop-filter: blur(2px);

        &.checked {
          background: #7367f0;
          border-color: #7367f0;
        }
      }

      .advertiser-info {
        padding: 2px 6px;
        background: rgba(0, 0, 0, 0.6);
        color: #fff;
        font-size: 12px;
        border-radius: 4px;
      }
    }

    &:hover .custom-play-button {
      opacity: 1;
      pointer-events: auto;
    }

    &:hover .mute-toggle {
      opacity: 1;
      pointer-events: auto;
    }

    &:hover .download-button {
      opacity: 1;
      pointer-events: auto;
    }

    .video-info {
      padding: 12px;

      h4 {
        margin: 0 0 8px 0;
        font-size: 14px;
        font-weight: 600;
        color: #333;
      }

      p {
        margin: 0;
        font-size: 12px;
        color: #666;
        line-height: 1.4;
      }
    }
  }

  .pagination-container {
    .pagination-table {
      margin-top: 1rem !important;
    }
    .page-size-control {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 12px;

      .page-size-label {
        font-size: 14px;
        white-space: nowrap;
      }

      .page-size-select {
        min-width: 60px;
        font-size: 13px;
        border: 1px solid #ced4da;
        border-radius: 4px;

        &:focus {
          border-color: #80bdff;
          box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
        }
      }
    }
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.spinning {
  animation: spin 1s linear infinite;
}
</style>
